import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabaseClient'

// ── Design tokens ──────────────────────────────────────────────
const C = {
  bg: '#0D0D0F', surface: '#16161A', card: '#1E1E24',
  border: '#2A2A32', accent: '#7C6FF7', accentSoft: '#3D3872',
  green: '#4ADE80', red: '#F87171', yellow: '#FBBF24',
  muted: '#6B6B80', text: '#F0F0F5', textSub: '#9999AA',
}

// ── Static data ────────────────────────────────────────────────
const DEFAULT_INVESTMENTS = [
  { id:'voo',     label:'VOO',               target:120000, category:'invest', icon:'📈' },
  { id:'qqq',     label:'QQQ',               target:120000, category:'invest', icon:'📈' },
  { id:'schd',    label:'SCHD',              target:80000,  category:'invest', icon:'💰' },
  { id:'isa',     label:'ISA (TIGER)',        target:200000, category:'invest', icon:'🏦' },
  { id:'irp',     label:'IRP',               target:200000, category:'invest', icon:'🏛️' },
  { id:'savings', label:'월복리 적금 ✈️',    target:200000, category:'save',   icon:'✈️' },
  { id:'emerg',   label:'비상금 (달달하나)', target:80000,  category:'save',   icon:'🛡️' },
  { id:'apt',     label:'주택청약',           target:20000,  category:'save',   icon:'🏠' },
  { id:'loan',    label:'대출 상환',          target:100000, category:'debt',   icon:'💳' },
]

const DEFAULT_EXPENSES = [
  { id:'rent',    label:'월세/관리비',        budget:500000, category:'fixed',  icon:'🏘️' },
  { id:'comm',    label:'통신비',             budget:55000,  category:'fixed',  icon:'📱' },
  { id:'sub',     label:'구독 (넷플릭스 등)', budget:30000,  category:'fixed',  icon:'🎬' },
  { id:'trans',   label:'교통/카카오T',       budget:100000, category:'fixed',  icon:'🚕' },
  { id:'insur',   label:'보험',               budget:100000, category:'fixed',  icon:'🔒' },
  { id:'sbux',    label:'스타벅스',           budget:80000,  category:'living', icon:'☕' },
  { id:'baemin',  label:'배달의민족',         budget:100000, category:'living', icon:'🛵' },
  { id:'conv',    label:'편의점',             budget:50000,  category:'living', icon:'🏪' },
  { id:'coupang', label:'쿠팡',              budget:120000, category:'living', icon:'📦' },
  { id:'mart',    label:'이마트/마트',        budget:100000, category:'living', icon:'🛒' },
  { id:'shop',    label:'쇼핑/뷰티',         budget:150000, category:'living', icon:'👗' },
  { id:'play',    label:'여가/취미',          budget:150000, category:'living', icon:'🎮' },
  { id:'dine',    label:'외식',               budget:150000, category:'living', icon:'🍽️' },
  { id:'misc',    label:'예비비',             budget:100000, category:'living', icon:'🔧' },
]

const JADE_RATES = { sbux:0.50, sub:0.50, trans:0.50, coupang:0.012, mart:0.012 }

const fmt = n => '₩' + Math.round(n).toLocaleString('ko-KR')
const pct = (a,b) => b===0 ? 0 : Math.min(100,(a/b)*100)
const getMonthKey = () => { const d=new Date(); return `${d.getFullYear()}-${d.getMonth()+1}` }
const getMonthLabel = () => { const d=new Date(); return `${d.getFullYear()}년 ${d.getMonth()+1}월` }

// ── Sub-components ─────────────────────────────────────────────
function Bar({ value, max, color=C.accent }) {
  const p = pct(value,max)
  const col = p>90 ? C.red : p>70 ? C.yellow : color
  return (
    <div style={{ background:C.border, borderRadius:99, height:5, overflow:'hidden' }}>
      <div style={{ width:p+'%', height:'100%', borderRadius:99,
        background:col, transition:'width .4s ease' }} />
    </div>
  )
}

function Pill({ label, color }) {
  return <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px',
    borderRadius:99, background:color+'22', color }}>{label}</span>
}

function StatCard({ label, value, sub, color=C.text }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`,
      borderRadius:16, padding:'18px 20px', flex:1, minWidth:140 }}>
      <div style={{ fontSize:11, color:C.muted, fontWeight:600,
        textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, color, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:C.textSub, marginTop:5 }}>{sub}</div>}
    </div>
  )
}

// ── Input component ────────────────────────────────────────────
function Input({ label, type='text', value, onChange, placeholder, error }) {
  const [show, setShow] = useState(false)
  const isPassword = type === 'password'
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ fontSize:12, color:C.muted, fontWeight:600,
        display:'block', marginBottom:6 }}>{label}</label>
      <div style={{ position:'relative' }}>
        <input
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{ width:'100%', background:C.surface,
            border:`1px solid ${error ? C.red : C.border}`,
            borderRadius:10, padding:'12px 14px',
            paddingRight: isPassword ? 44 : 14,
            color:C.text, fontSize:14, outline:'none',
            transition:'border .2s', boxSizing:'border-box' }}
        />
        {isPassword && (
          <button onClick={() => setShow(s=>!s)} style={{
            position:'absolute', right:12, top:'50%',
            transform:'translateY(-50%)', background:'none',
            border:'none', cursor:'pointer', fontSize:16,
            color:C.muted, padding:0 }}>
            {show ? '🙈' : '👁️'}
          </button>
        )}
      </div>
      {error && <div style={{ fontSize:11, color:C.red, marginTop:4 }}>⚠️ {error}</div>}
    </div>
  )
}

// ── Auth Screen (Login + Sign Up) ──────────────────────────────
function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState('login') // 'login' | 'signup'
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const reset = () => {
    setError('')
    setSuccess('')
    setName('')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
  }

  const handleLogin = async () => {
    if (!email || !password) { setError('이메일과 비밀번호를 입력해주세요.'); return }
    setLoading(true)
    setError('')
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError('이메일 또는 비밀번호가 올바르지 않아요.')
    } else {
      onLogin(data.session)
    }
    setLoading(false)
  }

  const handleSignUp = async () => {
    if (!name) { setError('이름을 입력해주세요.'); return }
    if (!email) { setError('이메일을 입력해주세요.'); return }
    if (password.length < 6) { setError('비밀번호는 6자 이상이어야 해요.'); return }
    if (password !== confirmPassword) { setError('비밀번호가 일치하지 않아요.'); return }
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } }
    })
    if (error) {
      setError(error.message)
    } else {
      setSuccess('가입 완료! 이메일을 확인하거나 바로 로그인해보세요.')
      setTimeout(() => { setMode('login'); reset(); }, 2000)
    }
    setLoading(false)
  }

  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex',
      flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24 }}>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        @keyframes spin { to { transform:rotate(360deg) } }
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
      `}</style>

      {/* Logo */}
      <div style={{ marginBottom:32, textAlign:'center', animation:'fadeIn .4s ease' }}>
        <div style={{ fontSize:52, marginBottom:12 }}>💜</div>
        <div style={{ fontSize:26, fontWeight:900, color:C.text }}>Yooni Finance</div>
        <div style={{ fontSize:13, color:C.muted, marginTop:6 }}>
          개인 재무 트래커 · FIRE 로드맵
        </div>
      </div>

      {/* Card */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`,
        borderRadius:24, padding:32, width:'100%', maxWidth:380,
        animation:'fadeIn .4s ease' }}>

        {/* Tab switcher */}
        <div style={{ display:'flex', background:C.surface, borderRadius:12,
          padding:4, marginBottom:24 }}>
          {[['login','로그인'],['signup','회원가입']].map(([m,l]) => (
            <button key={m} onClick={() => { setMode(m); reset() }} style={{
              flex:1, padding:'10px 0', border:'none', borderRadius:9,
              background: mode===m ? C.accent : 'transparent',
              color: mode===m ? '#fff' : C.muted,
              fontWeight:700, fontSize:13, cursor:'pointer',
              transition:'all .2s' }}>{l}</button>
          ))}
        </div>

        {/* Error / Success */}
        {error && (
          <div style={{ background:'#FF000015', border:`1px solid ${C.red}`,
            borderRadius:10, padding:'10px 14px', fontSize:13,
            color:C.red, marginBottom:16 }}>⚠️ {error}</div>
        )}
        {success && (
          <div style={{ background:'#4ADE8015', border:`1px solid ${C.green}`,
            borderRadius:10, padding:'10px 14px', fontSize:13,
            color:C.green, marginBottom:16 }}>✅ {success}</div>
        )}

        {/* Form */}
        {mode === 'signup' && (
          <Input label="이름" value={name}
            onChange={e => setName(e.target.value)}
            placeholder="유이니" />
        )}
        <Input label="이메일" type="email" value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="yooni@teamspotlite.com" />
        <Input label="비밀번호" type="password" value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="6자 이상" />
        {mode === 'signup' && (
          <Input label="비밀번호 확인" type="password" value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="비밀번호 재입력" />
        )}

        {/* Submit button */}
        <button
          onClick={mode==='login' ? handleLogin : handleSignUp}
          disabled={loading}
          onKeyDown={e => e.key==='Enter' && (mode==='login' ? handleLogin() : handleSignUp())}
          style={{ width:'100%', padding:'14px 0',
            background: loading ? C.accentSoft : C.accent,
            border:'none', borderRadius:12, color:'#fff',
            fontWeight:700, fontSize:15, cursor: loading ? 'not-allowed' : 'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap:10,
            marginTop:4, transition:'all .2s' }}>
          {loading ? (
            <>
              <div style={{ width:18, height:18, borderRadius:'50%',
                border:'2px solid #ffffff44', borderTop:'2px solid #fff',
                animation:'spin 1s linear infinite' }} />
              {mode==='login' ? '로그인 중...' : '가입 중...'}
            </>
          ) : (
            mode==='login' ? '🔐 로그인' : '✨ 회원가입'
          )}
        </button>

        {/* Data protection info */}
        <div style={{ marginTop:20, padding:'14px 16px',
          background:C.surface, borderRadius:12, border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:11, color:C.muted, fontWeight:600,
            textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>
            🔒 데이터 보호
          </div>
          {[
            '이메일 + 비밀번호로 안전 로그인',
            '모든 기기에서 데이터 동기화',
            '앱 업데이트해도 데이터 보존',
            'Supabase DB에 암호화 저장',
          ].map((t,i) => (
            <div key={i} style={{ fontSize:12, color:C.textSub,
              display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
              <span style={{ color:C.green }}>✓</span> {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── AI Advisor ─────────────────────────────────────────────────
function AIAdvisor({ summary, userName }) {
  const [messages, setMessages] = useState([{
    role:'assistant',
    text:`안녕하세요 ${userName}님! 💜 이번 달 데이터를 기반으로 뭐든 물어보세요.`
  }])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)

  const send = async (text) => {
    const msg = text || input.trim()
    if (!msg || loading) return
    setInput('')
    setMessages(p => [...p, { role:'user', text:msg }])
    setLoading(true)
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body:JSON.stringify({
          model:'claude-sonnet-4-6',
          max_tokens:800,
          system:`You are a senior Korean financial advisor advising Yooni, a UX strategist in Korea.
Monthly take-home: ₩3,200,000. FIRE target: ₩600M in 10-13 years.
Investments: VOO ₩120K, QQQ ₩120K, SCHD ₩80K, ISA ₩200K, IRP ₩200K monthly.
Cards: Jade Classic (Starbucks/subscriptions/taxi 50%; Coupang/Emart 1.2%).
Current month: ${JSON.stringify(summary)}
Answer in Korean, 2-3 sentences max, practical and specific.`,
          messages: [
            ...messages.slice(-4).map(m => ({ role:m.role, content:m.text })),
            { role:'user', content:msg }
          ]
        })
      })
      const data = await res.json()
      setMessages(p => [...p, { role:'assistant', text:data.content?.[0]?.text || '잠시 후 다시 시도해주세요.' }])
    } catch {
      setMessages(p => [...p, { role:'assistant', text:'연결 오류가 발생했어요.' }])
    }
    setLoading(false)
  }

  const QUICK = ['이번 달 분석', 'FIRE 달성 시기', 'IRP 얼마나 더?', 'Jade 혜택 팁', '절약 방법']

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ background:C.card, border:`1px solid ${C.accentSoft}`,
        borderRadius:20, overflow:'hidden' }}>
        <div style={{ padding:'14px 18px',
          background:`linear-gradient(135deg, ${C.accentSoft}, ${C.card})`,
          borderBottom:`1px solid ${C.border}`,
          display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:34, height:34, borderRadius:'50%', background:C.accent,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>💜</div>
          <div>
            <div style={{ fontWeight:700, fontSize:13 }}>AI 재무 어드바이저</div>
            <div style={{ fontSize:10, color:C.accent }}>● 현재 데이터 기반</div>
          </div>
        </div>
        <div style={{ height:220, overflowY:'auto', padding:'14px 16px',
          display:'flex', flexDirection:'column', gap:10 }}>
          {messages.map((m,i) => (
            <div key={i} style={{ display:'flex',
              justifyContent:m.role==='user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth:'82%', padding:'10px 13px', borderRadius:14,
                background:m.role==='user' ? C.accent : C.surface,
                color:C.text, fontSize:13, lineHeight:1.6,
                borderBottomRightRadius:m.role==='user' ? 4 : 14,
                borderBottomLeftRadius:m.role==='assistant' ? 4 : 14 }}>{m.text}</div>
            </div>
          ))}
          {loading && (
            <div style={{ display:'flex', gap:4, padding:'8px 12px' }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width:6, height:6, borderRadius:'50%',
                  background:C.accent,
                  animation:`bounce ${.5+i*.15}s infinite ease-in-out` }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ padding:'10px 14px', borderTop:`1px solid ${C.border}`,
          display:'flex', gap:8 }}>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key==='Enter' && send()}
            placeholder="무엇이든 물어보세요..."
            style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:10, padding:'9px 12px', color:C.text,
              fontSize:13, outline:'none' }} />
          <button onClick={() => send()} disabled={loading}
            style={{ background:C.accent, border:'none', borderRadius:10,
              padding:'9px 14px', color:'#fff', fontWeight:700, cursor:'pointer' }}>
            전송
          </button>
        </div>
      </div>
      <div style={{ background:C.card, borderRadius:14, padding:14 }}>
        <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:8 }}>빠른 질문</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {QUICK.map(q => (
            <button key={q} onClick={() => send(q)} style={{ background:C.surface,
              border:`1px solid ${C.border}`, borderRadius:99,
              padding:'6px 12px', color:C.textSub, fontSize:11, cursor:'pointer' }}>{q}</button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Main App ───────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [tab, setTab] = useState('dashboard')
  const [investments, setInvestments] = useState(DEFAULT_INVESTMENTS.map(i => ({ ...i, actual:0 })))
  const [expenses, setExpenses] = useState(DEFAULT_EXPENSES.map(e => ({ ...e, actual:0 })))
  const [history, setHistory] = useState([])
  const [savedAt, setSavedAt] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setAuthLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session)
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!session) return
    loadMonthData()
    loadHistory()
  }, [session])

  const loadMonthData = async () => {
    const { data } = await supabase
      .from('monthly_data')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('month_key', getMonthKey())
      .single()
    if (data) {
      setInvestments(data.investments || DEFAULT_INVESTMENTS.map(i => ({ ...i, actual:0 })))
      setExpenses(data.expenses || DEFAULT_EXPENSES.map(e => ({ ...e, actual:0 })))
    }
  }

  const loadHistory = async () => {
    const { data } = await supabase
      .from('monthly_data')
      .select('month_key, month_label, total_invest, total_expense, savings_rate')
      .eq('user_id', session.user.id)
      .order('month_key', { ascending:false })
      .limit(12)
    if (data) setHistory(data)
  }

  const saveData = useCallback(async (invs, exps) => {
    if (!session) return
    setSaving(true)
    const now = new Date().toLocaleTimeString('ko-KR', { hour:'2-digit', minute:'2-digit' })
    const totalInvest = invs.reduce((s,i) => s+i.actual, 0)
    const totalExpense = exps.reduce((s,e) => s+e.actual, 0)
    await supabase.from('monthly_data').upsert({
      user_id: session.user.id,
      month_key: getMonthKey(),
      month_label: getMonthLabel(),
      investments: invs,
      expenses: exps,
      total_invest: totalInvest,
      total_expense: totalExpense,
      savings_rate: Math.round((totalInvest/3200000)*100),
      updated_at: new Date().toISOString(),
    }, { onConflict:'user_id,month_key' })
    setSavedAt(now)
    setSaving(false)
  }, [session])

  useEffect(() => {
    if (!session) return
    const t = setTimeout(() => saveData(investments, expenses), 1000)
    return () => clearTimeout(t)
  }, [investments, expenses, session, saveData])

  const totalInvestTarget = investments.reduce((s,i) => s+i.target, 0)
  const totalInvestActual = investments.reduce((s,i) => s+i.actual, 0)
  const totalExpenseTarget = expenses.reduce((s,e) => s+e.budget, 0)
  const totalExpenseActual = expenses.reduce((s,e) => s+e.actual, 0)
  const remaining = 3200000 - totalInvestActual - totalExpenseActual
  const savingsRate = Math.round((totalInvestActual/3200000)*100)
  const jadePoints = expenses.reduce((sum,e) => sum+e.actual*(JADE_RATES[e.id]||0.01), 0)
  const fireProgress = pct(10355531 + totalInvestActual*3, 600000000)
  const userName = session?.user?.user_metadata?.name || session?.user?.email?.split('@')[0] || '유이니'

  const summary = {
    month:getMonthLabel(), income:fmt(3200000),
    invested:fmt(totalInvestActual), spent:fmt(totalExpenseActual),
    remaining:fmt(remaining), savingsRate:savingsRate+'%', jadePoints:fmt(jadePoints),
  }

  if (authLoading) return (
    <div style={{ background:C.bg, minHeight:'100vh', display:'flex',
      alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;}`}</style>
      <div style={{ fontSize:40 }}>💜</div>
      <div style={{ color:C.textSub }}>불러오는 중...</div>
    </div>
  )

  if (!session) return <AuthScreen onLogin={setSession} />

  const TABS = [
    { id:'dashboard', label:'홈',   icon:'📊' },
    { id:'invest',    label:'투자', icon:'📈' },
    { id:'expense',   label:'지출', icon:'💳' },
    { id:'history',   label:'기록', icon:'📅' },
    { id:'advisor',   label:'AI',   icon:'💜' },
  ]

  return (
    <div style={{ background:C.bg, minHeight:'100vh', color:C.text,
      fontFamily:"'Pretendard','Inter',sans-serif",
      maxWidth:480, margin:'0 auto', paddingBottom:80 }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;}
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button{-webkit-appearance:none;}
        input[type=number]{-moz-appearance:textfield;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-thumb{background:${C.border};border-radius:99px;}
        @keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {/* Header */}
      <div style={{ padding:'20px 20px 14px',
        background:`linear-gradient(180deg,${C.surface} 0%,${C.bg} 100%)`,
        position:'sticky', top:0, zIndex:10,
        borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:10, color:C.accent, fontWeight:700,
              letterSpacing:'.1em', textTransform:'uppercase' }}>YOONI FINANCE</div>
            <div style={{ fontSize:20, fontWeight:800, marginTop:2 }}>{getMonthLabel()}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ fontSize:10, color:saving ? C.yellow : C.green }}>
              {saving ? '⏳ 저장 중...' : savedAt ? `✓ ${savedAt}` : ''}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:'50%',
                background:C.accent, display:'flex', alignItems:'center',
                justifyContent:'center', fontWeight:800, fontSize:12 }}>
                {userName[0]?.toUpperCase()}
              </div>
              <button onClick={() => supabase.auth.signOut()} style={{
                background:'none', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'4px 10px',
                color:C.muted, fontSize:11, cursor:'pointer' }}>
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding:'18px 20px 0', animation:'fadeIn .3s ease' }}>

        {/* ── DASHBOARD ── */}
        {tab==='dashboard' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', gap:10 }}>
              <StatCard label="투자 완료" value={fmt(totalInvestActual)}
                sub={`목표 ${fmt(totalInvestTarget)}`} color={C.accent} />
              <StatCard label="지출 사용" value={fmt(totalExpenseActual)}
                sub={`예산 ${fmt(totalExpenseTarget)}`}
                color={totalExpenseActual>totalExpenseTarget ? C.red : C.green} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <StatCard label="저축률" value={savingsRate+'%'}
                sub="목표 30%+" color={savingsRate>=30 ? C.green : C.yellow} />
              <StatCard label="잔여 예산" value={fmt(Math.max(0,remaining))}
                color={remaining<0 ? C.red : C.text} />
            </div>

            {/* FIRE */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:20, padding:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between',
                alignItems:'center', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:600,
                    textTransform:'uppercase', letterSpacing:'.08em' }}>🔥 FIRE 달성률</div>
                  <div style={{ fontSize:12, color:C.textSub, marginTop:2 }}>목표 ₩600,000,000</div>
                </div>
                <div style={{ fontSize:26, fontWeight:900, color:C.accent }}>
                  {fireProgress.toFixed(1)}%
                </div>
              </div>
              <div style={{ background:C.border, borderRadius:99, height:8, overflow:'hidden' }}>
                <div style={{ width:fireProgress+'%', height:'100%', borderRadius:99,
                  background:`linear-gradient(90deg,${C.accent},#A78BFA)`,
                  transition:'width .8s ease' }} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between',
                marginTop:8, fontSize:11, color:C.textSub }}>
                <span>현재 ~₩10.3M</span>
                <span>예상 달성 ~2037년</span>
              </div>
            </div>

            {/* Jade */}
            <div style={{ background:`linear-gradient(135deg,#1A1830,${C.card})`,
              border:`1px solid ${C.accentSoft}`, borderRadius:20, padding:18,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:10, color:C.accent, fontWeight:700,
                  textTransform:'uppercase', letterSpacing:'.08em' }}>💎 Jade 하나머니 예상</div>
                <div style={{ fontSize:24, fontWeight:900, marginTop:4 }}>{fmt(jadePoints)}</div>
                <div style={{ fontSize:11, color:C.textSub, marginTop:3 }}>이번 달 지출 기준</div>
              </div>
              <div style={{ fontSize:32 }}>💳</div>
            </div>

            {/* Budget bars */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:20, padding:18 }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:14 }}>예산 현황</div>
              {[
                { label:'투자/저축', actual:totalInvestActual, target:totalInvestTarget, color:C.accent },
                { label:'고정 지출',
                  actual:expenses.filter(e=>e.category==='fixed').reduce((s,e)=>s+e.actual,0),
                  target:expenses.filter(e=>e.category==='fixed').reduce((s,e)=>s+e.budget,0),
                  color:C.yellow },
                { label:'변동 생활비',
                  actual:expenses.filter(e=>e.category==='living').reduce((s,e)=>s+e.actual,0),
                  target:expenses.filter(e=>e.category==='living').reduce((s,e)=>s+e.budget,0),
                  color:C.green },
              ].map((row,i) => (
                <div key={i} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between',
                    fontSize:12, marginBottom:5 }}>
                    <span style={{ color:C.textSub }}>{row.label}</span>
                    <span style={{ fontWeight:600 }}>
                      {fmt(row.actual)} <span style={{ color:C.muted }}>/ {fmt(row.target)}</span>
                    </span>
                  </div>
                  <Bar value={row.actual} max={row.target} color={row.color} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── INVEST ── */}
        {tab==='invest' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ fontSize:13, color:C.textSub }}>실제 납입한 금액을 입력하세요</div>
            {['invest','save','debt'].map(cat => {
              const items = investments.filter(i => i.category===cat)
              const labels = { invest:'📈 투자', save:'💰 저축', debt:'💳 부채' }
              return (
                <div key={cat} style={{ background:C.card, border:`1px solid ${C.border}`,
                  borderRadius:20, overflow:'hidden' }}>
                  <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.border}`,
                    fontSize:11, fontWeight:700, color:C.muted,
                    textTransform:'uppercase', letterSpacing:'.08em' }}>{labels[cat]}</div>
                  {items.map((item,i) => (
                    <div key={item.id} style={{ padding:'14px 18px',
                      borderBottom:i<items.length-1 ? `1px solid ${C.border}` : 'none' }}>
                      <div style={{ display:'flex', justifyContent:'space-between',
                        alignItems:'center', marginBottom:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:18 }}>{item.icon}</span>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600 }}>{item.label}</div>
                            <div style={{ fontSize:11, color:C.muted }}>목표 {fmt(item.target)}</div>
                          </div>
                        </div>
                        <input type="number" value={item.actual||''}
                          onChange={e => setInvestments(p => p.map(x =>
                            x.id===item.id ? { ...x, actual:Math.max(0,parseInt(e.target.value)||0) } : x
                          ))}
                          placeholder="0"
                          style={{ width:110, background:C.surface,
                            border:`1px solid ${item.actual>=item.target ? C.accent : C.border}`,
                            borderRadius:8, padding:'6px 10px', color:C.text,
                            fontSize:13, fontWeight:600, textAlign:'right', outline:'none' }} />
                      </div>
                      <Bar value={item.actual} max={item.target} color={C.accent} />
                      {item.actual>=item.target && (
                        <div style={{ fontSize:11, color:C.green, marginTop:4 }}>✓ 완료!</div>
                      )}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )}

        {/* ── EXPENSE ── */}
        {tab==='expense' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ fontSize:13, color:C.textSub }}>실제 사용한 금액을 입력하세요</div>
            {['fixed','living'].map(cat => {
              const items = expenses.filter(e => e.category===cat)
              const labels = { fixed:'🔒 고정 지출', living:'🛍️ 변동 생활비' }
              return (
                <div key={cat} style={{ background:C.card, border:`1px solid ${C.border}`,
                  borderRadius:20, overflow:'hidden' }}>
                  <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.border}`,
                    fontSize:11, fontWeight:700, color:C.muted,
                    textTransform:'uppercase', letterSpacing:'.08em' }}>{labels[cat]}</div>
                  {items.map((item,i) => {
                    const over = item.actual > item.budget
                    const jRate = JADE_RATES[item.id]
                    return (
                      <div key={item.id} style={{ padding:'14px 18px',
                        borderBottom:i<items.length-1 ? `1px solid ${C.border}` : 'none',
                        background:over ? '#FF000008' : 'transparent' }}>
                        <div style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'center', marginBottom:8 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:18 }}>{item.icon}</span>
                            <div>
                              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <span style={{ fontSize:13, fontWeight:600 }}>{item.label}</span>
                                {jRate && <Pill label={jRate>=0.5 ? '50% 적립' : '1.2%'}
                                  color={jRate>=0.5 ? C.accent : C.yellow} />}
                              </div>
                              <div style={{ fontSize:11, color:over ? C.red : C.muted }}>
                                예산 {fmt(item.budget)}
                                {jRate && item.actual>0 && (
                                  <span style={{ color:C.accent }}> · +{fmt(item.actual*jRate)} 하나머니</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <input type="number" value={item.actual||''}
                            onChange={e => setExpenses(p => p.map(x =>
                              x.id===item.id ? { ...x, actual:Math.max(0,parseInt(e.target.value)||0) } : x
                            ))}
                            placeholder="0"
                            style={{ width:110, background:C.surface,
                              border:`1px solid ${over ? C.red : C.border}`,
                              borderRadius:8, padding:'6px 10px',
                              color:over ? C.red : C.text,
                              fontSize:13, fontWeight:600, textAlign:'right', outline:'none' }} />
                        </div>
                        <Bar value={item.actual} max={item.budget}
                          color={over ? C.red : C.green} />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        )}

        {/* ── HISTORY ── */}
        {tab==='history' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ fontSize:13, color:C.textSub }}>월별 저축률과 지출 기록</div>
            {history.length===0 ? (
              <div style={{ background:C.card, border:`1px solid ${C.border}`,
                borderRadius:20, padding:40, textAlign:'center' }}>
                <div style={{ fontSize:32, marginBottom:12 }}>📅</div>
                <div style={{ fontSize:14, color:C.textSub }}>아직 기록이 없어요</div>
                <div style={{ fontSize:12, color:C.muted, marginTop:6 }}>
                  매달 데이터를 입력하면 자동으로 쌓여요
                </div>
              </div>
            ) : history.map((h,i) => (
              <div key={i} style={{ background:C.card, border:`1px solid ${C.border}`,
                borderRadius:16, padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between',
                  alignItems:'center', marginBottom:12 }}>
                  <div style={{ fontSize:14, fontWeight:700 }}>{h.month_label}</div>
                  <div style={{ fontSize:18, fontWeight:800,
                    color:h.savings_rate>=30 ? C.green : C.yellow }}>
                    저축률 {h.savings_rate}%
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <div style={{ flex:1, background:C.surface, borderRadius:10, padding:10 }}>
                    <div style={{ fontSize:10, color:C.muted }}>투자/저축</div>
                    <div style={{ fontSize:15, fontWeight:700, color:C.accent }}>
                      {fmt(h.total_invest)}</div>
                  </div>
                  <div style={{ flex:1, background:C.surface, borderRadius:10, padding:10 }}>
                    <div style={{ fontSize:10, color:C.muted }}>총 지출</div>
                    <div style={{ fontSize:15, fontWeight:700 }}>{fmt(h.total_expense)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── ADVISOR ── */}
        {tab==='advisor' && <AIAdvisor summary={summary} userName={userName} />}

      </div>

      {/* Bottom nav */}
      <div style={{ position:'fixed', bottom:0, left:'50%', transform:'translateX(-50%)',
        width:'100%', maxWidth:480, background:C.surface,
        borderTop:`1px solid ${C.border}`, display:'flex', padding:'8px 0 10px' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex:1, background:'none', border:'none', cursor:'pointer',
              display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <span style={{ fontSize:18 }}>{t.icon}</span>
            <span style={{ fontSize:9, fontWeight:600,
              color:tab===t.id ? C.accent : C.muted }}>{t.label}</span>
            {tab===t.id && <div style={{ width:4, height:4, borderRadius:'50%',
              background:C.accent }} />}
          </button>
        ))}
      </div>
    </div>
  )
}
